<template>
  <div class="flex select-none flex-col items-center gap-1.5">
    <span
      v-if="caption"
      class="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400"
      >{{ caption }}</span
    >

    <div
      class="relative rounded-full outline-none focus-visible:ring-2 focus-visible:ring-gray-400/60"
      :style="{ width: `${size}px`, height: `${size}px` }"
      role="slider"
      tabindex="0"
      :aria-label="caption ?? 'Duration'"
      :aria-valuemin="detents[0]"
      :aria-valuemax="detents[detents.length - 1]"
      :aria-valuenow="safeValue"
      :aria-valuetext="valueLabel"
      @keydown="onKeydown">
      <svg
        :width="size"
        :height="size"
        :viewBox="`0 0 ${size} ${size}`"
        class="block"
        :style="{ touchAction: 'none', cursor: dragging ? 'grabbing' : 'grab', overflow: 'visible' }"
        v-bind="svgHandlers">
        <!-- Track -->
        <path :d="trackPath" fill="none" :stroke="trackColor" :stroke-width="ringWidth" stroke-linecap="round" />

        <!-- Filled arc -->
        <path
          v-if="activePath"
          :d="activePath"
          fill="none"
          :stroke="color"
          :stroke-width="ringWidth"
          stroke-linecap="round" />

        <!-- Detent ticks -->
        <line
          v-for="tick in ticks"
          :key="tick.value"
          :x1="tick.x1"
          :y1="tick.y1"
          :x2="tick.x2"
          :y2="tick.y2"
          :stroke="tick.major ? majorTickColor : minorTickColor"
          :stroke-width="tick.major ? 1.6 : 1"
          stroke-linecap="round" />

        <!-- Detent labels -->
        <text
          v-for="label in tickLabels"
          :key="`label-${label.value}`"
          :x="label.x"
          :y="label.y"
          text-anchor="middle"
          dominant-baseline="central"
          :fill="label.active ? color : labelColor"
          :font-size="labelFontSize"
          :font-weight="label.active ? 700 : 500"
          font-family="Inter, system-ui, sans-serif">
          {{ label.text }}
        </text>

        <!-- Handle -->
        <circle :cx="handleX" :cy="handleY" :r="handleR * 1.55" :fill="color" opacity="0.18" />
        <circle :cx="handleX" :cy="handleY" :r="handleR" :fill="color" />
        <circle :cx="handleX" :cy="handleY" r="3" fill="white" opacity="0.9" />
      </svg>

      <!-- Center value -->
      <div class="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
        <span
          class="font-bold leading-none tabular-nums text-gray-900 dark:text-white"
          :style="{ fontSize: `${valueFontSize}px` }"
          >{{ valueLabel }}</span
        >
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { formatDurationLabel } from '../../utils/switchAutomation';

/**
 * Rotary (knob) duration selector.
 *
 * The dial sweeps 270° (gap at the bottom) and steps through a curated list of
 * detent values — fine granularity for short timers, coarser towards 24 h.
 * Drag around the ring or tap a mark to jump straight to it; arrow keys,
 * PageUp/PageDown, Home and End work when focused.
 */

/** Only a subset of the detents gets a printed label, so they never collide. */
const STOP_LABELS: Record<number, string> = {
  15: '15s',
  60: '1m',
  300: '5m',
  900: '15m',
  1800: '30m',
  3600: '1h',
  7200: '2h',
  14400: '4h',
  86400: '24h',
};

interface Props {
  /** Selected duration in seconds. */
  modelValue: number;
  /** Optional caption rendered above the dial. */
  caption?: string;
  /** Accent color used for the active arc, handle and labels. */
  color?: string;
  /** Dial diameter in px. */
  size?: number;
  /** Upper bound for selectable durations (detents above it are dropped). */
  maxSeconds?: number;
  /** Override the detent list (seconds, ascending). */
  stops?: number[];
}

const props = withDefaults(defineProps<Props>(), {
  caption: undefined,
  color: 'rgb(16, 185, 129)',
  size: 200,
  maxSeconds: 86400,
  // Inlined (not a module constant) — defineProps defaults are hoisted.
  stops: () => [
    15, 30, 45, 60, 90, 120, 180, 240, 300, 420, 600, 900, 1200, 1800, 2700, 3600, 5400, 7200, 10800, 14400, 21600,
    28800, 43200, 86400,
  ],
});

const emit = defineEmits<{
  'update:modelValue': [value: number];
}>();

// ── colors (rgba neutrals so light + dark both work) ──
const trackColor = 'rgba(128,128,128,0.16)';
const majorTickColor = 'rgba(128,128,128,0.55)';
const minorTickColor = 'rgba(128,128,128,0.28)';
const labelColor = 'rgba(128,128,128,0.85)';

// ── geometry ──
const START_ANGLE = 135; // bottom-left, screen degrees (0° = 3 o'clock)
const SWEEP = 270; // ends bottom-right

const CX = computed(() => props.size / 2);
const CY = computed(() => props.size / 2);
const ringRadius = computed(() => props.size / 2 - 38);
const ringWidth = computed(() => Math.max(8, props.size * 0.055));
const tickInner = computed(() => ringRadius.value + 8);
const tickMinorOuter = computed(() => ringRadius.value + 12);
const tickMajorOuter = computed(() => ringRadius.value + 17);
const labelRadius = computed(() => ringRadius.value + 28);
const labelFontSize = computed(() => Math.max(8, props.size * 0.046));
const valueFontSize = computed(() => Math.max(16, props.size * 0.105));
const handleR = computed(() => Math.max(6, props.size * 0.042));

// ── detents ──
const detents = computed(() => {
  const list = props.stops
    .filter((value) => Number.isFinite(value) && value > 0 && value <= props.maxSeconds)
    .sort((a, b) => a - b);
  const unique = [...new Set(list)];
  return unique.length ? unique : [props.maxSeconds];
});

const safeValue = computed(() => (Number.isFinite(props.modelValue) ? Math.max(0, props.modelValue) : 0));

const valueLabel = computed(() => formatDurationLabel(safeValue.value));

function angleForIndex(index: number): number {
  const last = Math.max(1, detents.value.length - 1);
  return START_ANGLE + (index / last) * SWEEP;
}

function polar(radius: number, angleDeg: number): [number, number] {
  const rad = (angleDeg * Math.PI) / 180;
  return [CX.value + radius * Math.cos(rad), CY.value + radius * Math.sin(rad)];
}

/** Fractional position of the current value between detents (for a smooth handle). */
const continuousIndex = computed(() => {
  const list = detents.value;
  const value = safeValue.value;
  const last = list.length - 1;
  if (value <= list[0]) return 0;
  if (value >= list[last]) return last;

  for (let i = 0; i < last; i += 1) {
    if (value >= list[i] && value <= list[i + 1]) {
      const span = list[i + 1] - list[i];
      return span > 0 ? i + (value - list[i]) / span : i;
    }
  }
  return last;
});

const activeIndex = computed(() => Math.min(detents.value.length - 1, Math.max(0, Math.round(continuousIndex.value))));

function arcPath(fromIndex: number, toIndex: number): string {
  const start = angleForIndex(fromIndex);
  const end = angleForIndex(toIndex);
  const sweep = end - start;
  if (sweep <= 0.01) return '';

  const [sx, sy] = polar(ringRadius.value, start);
  const [ex, ey] = polar(ringRadius.value, end);
  const largeArc = sweep > 180 ? 1 : 0;
  const r = ringRadius.value.toFixed(2);

  return `M ${sx.toFixed(2)} ${sy.toFixed(2)} A ${r} ${r} 0 ${largeArc} 1 ${ex.toFixed(2)} ${ey.toFixed(2)}`;
}

const trackPath = computed(() => arcPath(0, detents.value.length - 1));
const activePath = computed(() => arcPath(0, continuousIndex.value));

const ticks = computed(() =>
  detents.value.map((value, index) => {
    const angle = (angleForIndex(index) * Math.PI) / 180;
    const major = STOP_LABELS[value] !== undefined;
    const inner = tickInner.value;
    const outer = major ? tickMajorOuter.value : tickMinorOuter.value;

    return {
      value,
      major,
      x1: (CX.value + inner * Math.cos(angle)).toFixed(2),
      y1: (CY.value + inner * Math.sin(angle)).toFixed(2),
      x2: (CX.value + outer * Math.cos(angle)).toFixed(2),
      y2: (CY.value + outer * Math.sin(angle)).toFixed(2),
    };
  }),
);

const tickLabels = computed(() =>
  detents.value.flatMap((value, index) => {
    const text = STOP_LABELS[value];
    if (!text) return [];

    const angle = (angleForIndex(index) * Math.PI) / 180;
    return [
      {
        value,
        text,
        active: Math.abs(safeValue.value - value) < 0.5,
        x: (CX.value + labelRadius.value * Math.cos(angle)).toFixed(2),
        y: (CY.value + labelRadius.value * Math.sin(angle)).toFixed(2),
      },
    ];
  }),
);

const handleX = computed(() => {
  const [x] = polar(ringRadius.value, angleForIndex(continuousIndex.value));
  return x.toFixed(2);
});

const handleY = computed(() => {
  const [, y] = polar(ringRadius.value, angleForIndex(continuousIndex.value));
  return y.toFixed(2);
});

// ── interaction ──
const dragging = ref(false);
let dragPointerId: number | null = null;

function indexFromPointer(clientX: number, clientY: number, svg: SVGSVGElement): number {
  const rect = svg.getBoundingClientRect();
  if (!rect.width || !rect.height) return activeIndex.value;

  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  let deg = (Math.atan2(clientY - cy, clientX - cx) * 180) / Math.PI;

  if (deg < START_ANGLE) {
    if (deg > 45) {
      // pointer sits in the bottom gap → clamp to the nearer edge
      deg = deg < 90 ? 45 : START_ANGLE;
    } else {
      deg += 360;
    }
  }

  const count = detents.value.length;
  const ratio = (deg - START_ANGLE) / SWEEP;
  return Math.min(count - 1, Math.max(0, Math.round(ratio * (count - 1))));
}

function commitIndex(index: number) {
  const count = detents.value.length;
  const value = detents.value[Math.min(count - 1, Math.max(0, index))];
  if (value !== props.modelValue) emit('update:modelValue', value);
}

function onPointerDown(event: PointerEvent) {
  const svg = event.currentTarget as SVGSVGElement;
  dragPointerId = event.pointerId;
  dragging.value = true;
  if (svg.setPointerCapture) svg.setPointerCapture(event.pointerId);
  commitIndex(indexFromPointer(event.clientX, event.clientY, svg));
  event.preventDefault();
}

function onPointerMove(event: PointerEvent) {
  if (dragPointerId !== event.pointerId) return;
  commitIndex(indexFromPointer(event.clientX, event.clientY, event.currentTarget as SVGSVGElement));
}

function onPointerUp(event: PointerEvent) {
  if (dragPointerId !== event.pointerId) return;

  const svg = event.currentTarget as SVGSVGElement;
  if (svg.hasPointerCapture?.(event.pointerId)) svg.releasePointerCapture(event.pointerId);

  dragPointerId = null;
  dragging.value = false;
}

function onKeydown(event: KeyboardEvent) {
  const count = detents.value.length;
  let index = activeIndex.value;

  switch (event.key) {
    case 'ArrowRight':
    case 'ArrowUp':
      index += 1;
      break;
    case 'ArrowLeft':
    case 'ArrowDown':
      index -= 1;
      break;
    case 'PageUp':
      index += 5;
      break;
    case 'PageDown':
      index -= 5;
      break;
    case 'Home':
      index = 0;
      break;
    case 'End':
      index = count - 1;
      break;
    default:
      return;
  }

  event.preventDefault();
  commitIndex(index);
}

const svgHandlers = {
  onPointerdown: onPointerDown,
  onPointermove: onPointerMove,
  onPointerup: onPointerUp,
  onPointercancel: onPointerUp,
};
</script>
